import { useEffect, createContext, type PropsWithChildren, useCallback, useState } from 'react';

import axios from 'axios';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { type ContentItem } from './components/ClassSessions/ClassSessionPage/ContentTypes/types';
import { GET_CLASS_SESSION_DASHBOARD_DATA, GET_CLASS_SESSION_CONTENT } from '../../config/apiPaths';
import { getUrls } from '../../utils/url';

export interface ClassSessionProviderProps {
    classSessionUuid: string;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface ClassSessionContextInterface {
    classSessionUuid: string | null;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    errorMessage: string;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    classSessionData: any;
    setClassSessionData: (classSessionData: any) => void;
    onBack?: () => void;
    me: any;
    meFetched: boolean;
    classSessionContent: { loading: boolean; items: ContentItem[] };
    classSessionLog: any;
    classSessionReportingEvents: any;
}

const ClassSessionContext = createContext(undefined as unknown as ClassSessionContextInterface);

const { Provider, Consumer } = ClassSessionContext;

const ClassSessionProvider = (props: PropsWithChildren<ClassSessionProviderProps>) => {
    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem('user_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { fullScreen, narrowStyle, classSessionUuid, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [classSessionData, setClassSessionData] = useState<any>({ loading: true, students: [] });
    const [classSessionContent, setClassSessionContent] = useState<ClassSessionContextInterface['classSessionContent']>(
        {
            loading: true,
            items: []
        }
    );
    const [classSessionLog, setClassSessionLog] = useState<any>({ loading: true, items: [] });
    const [classSessionReportingEvents, setClassSessionReportingEvents] = useState<any>({ loading: true, items: [] });

    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [me, setMe] = useState<any>({});
    const [meFetched, setMeFetched] = useState<boolean>(false);
    const { api } = getUrls();

    console.log('classSessionProvider classSessionUuid', classSessionUuid);

    const fetchClassSessionData = useCallback(async () => {
        try {
            // should get extra data not base data
            const { data: responseData } = await axios.get(
                `${api}${GET_CLASS_SESSION_DASHBOARD_DATA}?user_uuid=${userUuid}&class_session_uuid=${classSessionUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );

            let data;

            if (!responseData.response_too_large_url) {
                data = responseData;
            } else {
                const { data: resData } = await axios.get(responseData.response_too_large_url);
                data = resData;
            }

            const studentsById: any = {};

            data.students.forEach((student) => {
                studentsById[student.student_id] = student;
            });

            const { data: responseContentData } = await axios.get(
                `${api}${GET_CLASS_SESSION_CONTENT}?meeting_id=${data.class.meeting_id}&user_uuid=${userUuid}&class_session_uuid=${classSessionUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );

            let contentData;

            if (!responseContentData.response_too_large_url) {
                contentData = responseContentData;
            } else {
                const { data: resData } = await axios.get(responseContentData.response_too_large_url);
                contentData = resData;
            }

            const items: ContentItem[] = [];
            contentData.tabs.forEach((tab) => {
                const student = studentsById[tab.student_id];
                items.push({
                    type: 'tab',
                    text_content: `${tab.title}`,
                    data: tab,
                    timestamp: tab.open_timestamp,
                    student
                });
            });
            contentData.chats.forEach((chat) => {
                const student = studentsById[chat.data?.from?.student_id];
                items.push({
                    type: 'chat',
                    text_content: `${chat.data?.from?.name}: ${chat.data.content}`,
                    data: chat,
                    timestamp: chat.timestamp,
                    student
                });
            });
            contentData.notes.forEach((note) => {
                if (note.type === 'note') {
                    const student = studentsById[note.student_id];
                    items.push({
                        type: 'note',
                        text_content: `${note.content}`,
                        data: note,
                        timestamp: note.timestamp,
                        student
                    });
                } else if (note.type === 'transcript') {
                    const student = studentsById[note.data.student_id];
                    items.push({
                        type: 'transcript',
                        text_content: `${note.data.user_name}: ${note.content}`,
                        data: note,
                        timestamp: note.timestamp,
                        student
                    });
                } else if (note.type === 'ask') {
                    const student = studentsById[note.student_id];
                    if (!student) {
                        return;
                    }
                    items.push({
                        type: 'ai',
                        text_content: `${student?.user_name}: ${note.data.content}`,
                        data: note,
                        timestamp: note.timestamp,
                        student
                    });
                }
            });
            contentData.classEvents.forEach((classEvent) => {
                let student;
                if (classEvent.student_id) {
                    student = studentsById[classEvent.student_id];
                }
                if (classEvent.type === 'api_error' || classEvent.type === 'client_error') {
                    items.push({
                        type: 'error',
                        text_content: 'error',
                        data: classEvent,
                        timestamp: Number(classEvent.timestamp),
                        student
                    });
                } else if (classEvent.type !== 'class_session_state_updated') {
                    items.push({
                        type: 'event',
                        text_content: classEvent.type,
                        data: classEvent,
                        timestamp: Number(classEvent.timestamp),
                        student
                    });
                } else if (classEvent.type === 'class_session_state_updated') {
                    items.push({
                        type: 'class_session_state_update',
                        text_content: classEvent.type,
                        data: classEvent,
                        timestamp: Number(classEvent.timestamp),
                        student
                    });
                }
            });
            items.sort((a, b) => a.timestamp - b.timestamp);
            setClassSessionData(data);
            setMe(data);
            setClassSessionContent({ loading: false, items });
            setClassSessionReportingEvents({ loading: false, items: contentData.reportingEvents });
            const logItems: ContentItem[] = [];
            contentData.reportingEvents.forEach((event) => {
                const student = studentsById[event.zoomstate_student_id];
                if (!student) {
                    return;
                }
                if (event.event_type === 'ATTENDANCE') {
                    logItems.push({
                        type: event.event_type,
                        text_content: `${student?.user_name} joined`,
                        timestamp: Number(event.start_timestamp),
                        data: event,
                        student
                    });
                    logItems.push({
                        type: event.event_type,
                        text_content: `${student?.user_name} left`,
                        timestamp: Number(event.end_timestamp),
                        data: event,
                        student
                    });
                }
                if (event.event_type === 'IS_TALKING') {
                    if (event.event_value === 'true') {
                        logItems.push({
                            type: event.event_type,
                            text_content: `${student?.user_name} stopped talking`,
                            timestamp: Number(event.end_timestamp),
                            data: event,
                            student
                        });
                    } else {
                        logItems.push({
                            type: event.event_type,
                            text_content: `${student?.user_name} started talking`,
                            timestamp: Number(event.end_timestamp),
                            data: event,
                            student
                        });
                    }
                }
                if (event.event_type === 'RAISED_HAND') {
                    if (event.event_value === 'true') {
                        logItems.push({
                            type: event.event_type,
                            text_content: `${student?.user_name} lowered their hand`,
                            timestamp: Number(event.end_timestamp),
                            data: event,
                            student
                        });
                    } else {
                        logItems.push({
                            type: event.event_type,
                            text_content: `${student?.user_name} raised their hand`,
                            timestamp: Number(event.end_timestamp),
                            data: event,
                            student
                        });
                    }
                }
                if (event.event_type === 'STARS') {
                    logItems.push({
                        type: event.event_type,
                        text_content: `${student?.user_name} was given ${event.event_value} stars`,
                        timestamp: Number(event.end_timestamp),
                        data: event,
                        student
                    });
                }
            });
            contentData.tabs.forEach((tab) => {
                const student = studentsById[tab.student_id];
                if (!student) {
                    return;
                }
                logItems.push({
                    type: 'TAB',
                    text_content: `${tab.title} ${tab.data?.url || ''} was launched`,
                    data: tab,
                    timestamp: tab.open_timestamp,
                    student
                });
            });
            contentData.chats.forEach((chat) => {
                const student = studentsById[chat.data?.from?.student_id];
                if (!student) {
                    return;
                }
                logItems.push({
                    type: 'CHAT',
                    text_content: `${chat.data?.from?.name}: ${chat.data.content}`,
                    data: chat,
                    timestamp: chat.timestamp,
                    student
                });
            });
            contentData.classEvents.forEach((classEvent) => {
                if (classEvent.type === 'api_error' || classEvent.type === 'client_error') {
                    logItems.push({
                        type: 'ERROR',
                        text_content: 'error',
                        data: classEvent,
                        timestamp: Number(classEvent.timestamp),
                        student: undefined
                    });
                } else if (classEvent.type !== 'class_session_state_updated') {
                    logItems.push({
                        type: 'EVENT',
                        text_content: classEvent.type,
                        data: classEvent,
                        timestamp: Number(classEvent.timestamp),
                        student: undefined
                    });
                } else if (classEvent.type === 'class_session_state_updated') {
                    logItems.push({
                        type: 'CLASS_SESSION_STATE_UPDATE',
                        text_content: classEvent.type,
                        data: classEvent,
                        timestamp: Number(classEvent.timestamp),
                        student: undefined
                    });
                }
            });
            contentData.notes.forEach((note) => {
                let student = studentsById[note.student_id];
                if (!student) {
                    return;
                }
                if (note.type === 'note') {
                    logItems.push({
                        type: 'NOTE',
                        text_content: `${note.content}`,
                        data: note,
                        timestamp: note.timestamp,
                        student
                    });
                } else if (note.type === 'transcript') {
                    student = studentsById[note.data.student_id];
                    logItems.push({
                        type: 'TRANSCRIPT',
                        text_content: `${student?.user_name}: ${note.content}`,
                        data: note,
                        timestamp: note.timestamp,
                        student
                    });
                } else if (note.type === 'ask') {
                    logItems.push({
                        type: 'AI',
                        text_content: `${note.content}`,
                        data: note,
                        timestamp: note.timestamp,
                        student
                    });
                }
            });
            logItems.sort((a, b) => a.timestamp - b.timestamp);
            setClassSessionLog({ loading: false, items: logItems });
            setIsLoading(false);
        } catch (err) {
            setErrorMessage(err!.toString());
            setClassSessionContent({ loading: false, items: [] });
            setClassSessionLog({ loading: false, items: [] });
            setIsLoading(false);
        }
    }, [api, authMode, classSessionUuid, userUuid, showDemoContent, setMe]);

    useEffect(() => {
        if (!classSessionUuid) {
            return;
        }

        const fetchMe = async () => {
            fetchClassSessionData();
            setMeFetched(true);
        };

        fetchMe();
    }, [api, userUuid, fetchClassSessionData, authMode, classSessionUuid, showDemoContent]);

    return (
        <Provider
            value={{
                classSessionUuid,
                loading: classSessionData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                classSessionData,
                setClassSessionData,
                me,
                meFetched,
                errorMessage,
                classSessionContent,
                classSessionLog,
                classSessionReportingEvents
            }}
        >
            {children}
        </Provider>
    );
};

export { ClassSessionContext, ClassSessionProvider, Consumer as ClassSessionConsumer };
